import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '../../lib/api'
import { useAuth } from '../../context/AuthContext'
import { MfaSettings } from '../../components/MfaSettings'

const MFA_ELIGIBLE_ROLES = new Set(['super_admin', 'atuune_admin'])

export default function AccountSettings() {
  const navigate = useNavigate()
  const { me, refreshMe } = useAuth()
  const [loading, setLoading] = useState(true)
  const [email, setEmail] = useState('')
  const [marketingOptIn, setMarketingOptIn] = useState(false)
  const [savingPrefs, setSavingPrefs] = useState(false)
  const [prefsStatus, setPrefsStatus] = useState<string | null>(null)

  const [exportBusy, setExportBusy] = useState(false)
  const [exportStatus, setExportStatus] = useState<string | null>(null)

  const [confirmingDeletion, setConfirmingDeletion] = useState(false)
  const [deletionBusy, setDeletionBusy] = useState(false)
  const [deletionStatus, setDeletionStatus] = useState<string | null>(null)

  useEffect(() => {
    api('/account/me')
      .then((data) => {
        setEmail(data.email)
        setMarketingOptIn(data.marketing_emails_opted_in)
      })
      .finally(() => setLoading(false))
  }, [])

  async function savePreferences() {
    setSavingPrefs(true)
    setPrefsStatus(null)
    try {
      await api('/account/marketing-preferences', {
        method: 'PATCH',
        body: JSON.stringify({ marketing_emails_opted_in: marketingOptIn }),
      })
      setPrefsStatus('Preferences saved.')
    } catch (err) {
      setPrefsStatus(err instanceof ApiError ? err.message : 'Could not save — try again.')
    } finally {
      setSavingPrefs(false)
    }
  }

  async function requestExport() {
    setExportBusy(true)
    setExportStatus(null)
    try {
      await api('/account/request-data-export', { method: 'POST' })
      setExportStatus("Request received — our team will follow up with your data export.")
    } catch (err) {
      setExportStatus(err instanceof ApiError ? err.message : 'Could not submit your request — try again.')
    } finally {
      setExportBusy(false)
    }
  }

  async function requestDeletion() {
    setDeletionBusy(true)
    setDeletionStatus(null)
    try {
      await api('/account/request-account-deletion', { method: 'POST' })
      setDeletionStatus('Request received — our team will follow up to complete your account deletion.')
      setConfirmingDeletion(false)
    } catch (err) {
      setDeletionStatus(err instanceof ApiError ? err.message : 'Could not submit your request — try again.')
    } finally {
      setDeletionBusy(false)
    }
  }

  if (loading) return <div className="page-loading">Loading your account…</div>

  return (
    <div>
      <div className="page-head">
        <button className="btn small outline" onClick={() => navigate(-1)} style={{ marginBottom: 10 }}>← Back</button>
        <h1>Account Settings</h1>
        <div className="ph-sub">{email}</div>
      </div>

      {me?.mfa_enrollment_required && (
        <div className="card" style={{ maxWidth: 560, marginBottom: 16, borderColor: 'var(--red)' }}>
          <h3 style={{ margin: 0 }}>Two-factor authentication is required</h3>
          <p style={{ fontSize: 13.5, marginTop: 6 }}>
            Your account role requires two-factor authentication before you can access anything else in
            ATUUNE. Complete setup below to continue.
          </p>
        </div>
      )}

      {me && MFA_ELIGIBLE_ROLES.has(me.role) && <MfaSettings onMfaStatusChange={refreshMe} />}

      <div className="card" style={{ maxWidth: 560, marginBottom: 16 }}>
        <h3>Data Export</h3>
        <p className="muted" style={{ fontSize: 13.5 }}>
          Request a copy of the personal information associated with your account.
        </p>
        <button className="btn outline" onClick={requestExport} disabled={exportBusy}>
          {exportBusy ? 'Submitting…' : 'Request Data Export'}
        </button>
        {exportStatus && <p role="status" style={{ fontSize: 13.5, marginTop: 10 }}>{exportStatus}</p>}
      </div>

      <div className="card" style={{ maxWidth: 560, marginBottom: 16 }}>
        <h3>Delete Account</h3>
        <p className="muted" style={{ fontSize: 13.5 }}>
          Request deletion of your account and associated personal information. This cannot be undone
          once completed.
        </p>
        {!confirmingDeletion ? (
          <button className="btn outline" onClick={() => setConfirmingDeletion(true)} disabled={deletionBusy}>
            Request Account Deletion
          </button>
        ) : (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5 }}>Are you sure? This will start the account deletion process.</span>
            <button className="btn small outline" onClick={() => setConfirmingDeletion(false)} disabled={deletionBusy}>Cancel</button>
            <button className="btn small" onClick={requestDeletion} disabled={deletionBusy}>
              {deletionBusy ? 'Submitting…' : 'Confirm deletion request'}
            </button>
          </div>
        )}
        {deletionStatus && <p role="status" style={{ fontSize: 13.5, marginTop: 10 }}>{deletionStatus}</p>}
      </div>

      <div className="card" style={{ maxWidth: 560 }}>
        <h3>Marketing Email Preferences</h3>
        <p className="muted" style={{ fontSize: 13.5 }}>
          ATUUNE doesn't send marketing emails today — this preference is saved for when it does.
        </p>
        <div className="field" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
          <input type="checkbox" id="marketingOptIn" checked={marketingOptIn} onChange={(e) => setMarketingOptIn(e.target.checked)} />
          <label htmlFor="marketingOptIn" style={{ margin: 0 }}>I'd like to receive occasional product and marketing emails from ATUUNE</label>
        </div>
        <button className="btn outline" onClick={savePreferences} disabled={savingPrefs} style={{ marginTop: 10 }}>
          {savingPrefs ? 'Saving…' : 'Save preferences'}
        </button>
        {prefsStatus && <p role="status" style={{ fontSize: 13.5, marginTop: 10 }}>{prefsStatus}</p>}
      </div>
    </div>
  )
}
